import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import {
  getDemoDataStatus,
  populateDemoData,
  flushDemoData,
  type DemoDataCounts,
  type TenantSummary,
} from '@/hooks/useSuperadmin';
import { useToast } from '@/hooks/use-toast';

const TABLE_LABELS: Record<keyof DemoDataCounts, string> = {
  projects_sync: 'projects',
  tasks_sync: 'tasks',
  kpis_sync: 'KPIs',
  meetings_sync: 'meetings',
  emails_sync: 'emails',
  risk_signals: 'risk signals',
  documents: 'documents',
  decisions: 'decisions',
  user_activities: 'activity events',
};

interface DemoDataDialogProps {
  tenant: TenantSummary | null;
  onOpenChange: (open: boolean) => void;
}

export function DemoDataDialog({ tenant, onOpenChange }: DemoDataDialogProps) {
  const { toast } = useToast();
  const [counts, setCounts] = useState<DemoDataCounts | null>(null);
  const [mockDataEnabled, setMockDataEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Re-seed flushes before it populates, so it carries the identical
  // blast radius as a plain Flush — both route through this one typed
  // confirmation rather than Re-seed getting a bare, unprotected click.
  const [confirmAction, setConfirmAction] = useState<'flush' | 'reseed' | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [statusError, setStatusError] = useState(false);

  const refresh = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    setStatusError(false);
    try {
      const result = await getDemoDataStatus(tenant.id);
      setCounts(result.counts);
      setMockDataEnabled(result.mockDataEnabled ?? null);
    } catch (e) {
      setStatusError(true);
      toast({ title: 'Failed to load demo data status', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tenant, toast]);

  useEffect(() => {
    setCounts(null);
    setMockDataEnabled(null);
    setStatusError(false);
    setConfirmAction(null);
    setConfirmText('');
    if (tenant) refresh();
  }, [tenant, refresh]);

  if (!tenant) return null;

  // user_activities is excluded here: it's written by ordinary app use
  // (useActivityTracking fires on every tracked action), so a tenant that
  // has ever been logged into has non-zero user_activities regardless of
  // whether any demo data was ever seeded. Counting it would make isEmpty
  // false on an unseeded-but-used tenant, hiding "Populate" and forcing a
  // superadmin toward Re-seed/Flush — both destructive — on first use.
  const isEmpty = counts
    ? Object.entries(counts)
        .filter(([table]) => table !== 'user_activities')
        .every(([, count]) => count === 0)
    : false;

  const handlePopulate = async () => {
    setBusy(true);
    try {
      const result = await populateDemoData(tenant.id);
      if (!result.success) {
        toast({ title: 'Populate completed with errors', description: result.errors?.map((e) => `${e.table}: ${e.message}`).join('; '), variant: 'destructive' });
      } else {
        toast({ title: `Demo data populated for ${tenant.name}` });
      }
      setCounts(result.counts);
      setMockDataEnabled(result.mockDataEnabled ?? null);
    } catch (e) {
      toast({ title: 'Populate failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleFlush = async () => {
    setBusy(true);
    try {
      const result = await flushDemoData(tenant.id);
      if (!result.success) {
        toast({ title: 'Flush completed with errors', description: result.errors?.map((e) => `${e.table}: ${e.message}`).join('; '), variant: 'destructive' });
      } else {
        toast({ title: `Demo data flushed for ${tenant.name}` });
      }
      setCounts(result.counts);
      setMockDataEnabled(result.mockDataEnabled ?? null);
    } catch (e) {
      toast({ title: 'Flush failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusy(false);
      setConfirmAction(null);
      setConfirmText('');
    }
  };

  const handleReseed = async () => {
    setBusy(true);
    try {
      await flushDemoData(tenant.id);
      const result = await populateDemoData(tenant.id);
      if (!result.success) {
        toast({ title: 'Re-seed completed with errors', description: result.errors?.map((e) => `${e.table}: ${e.message}`).join('; '), variant: 'destructive' });
      } else {
        toast({ title: `Demo data re-seeded for ${tenant.name}` });
      }
      setCounts(result.counts);
      setMockDataEnabled(result.mockDataEnabled ?? null);
    } catch (e) {
      toast({ title: 'Re-seed failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusy(false);
      setConfirmAction(null);
      setConfirmText('');
    }
  };

  return (
    <>
      <Dialog open={!!tenant && !confirmAction} onOpenChange={(open) => !open && onOpenChange(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demo Data — {tenant.name}</DialogTitle>
            <DialogDescription>
              Populate or flush realistic test data for this demo tenant. Only tenants with is_demo=true can use this.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : counts ? (
            <div className="grid grid-cols-3 gap-2 text-sm py-2">
              {(Object.keys(counts) as Array<keyof DemoDataCounts>).map((key) => (
                <div key={key} className="flex justify-between border rounded px-2 py-1">
                  <span className="text-muted-foreground">{TABLE_LABELS[key]}</span>
                  <span className="font-semibold">{counts[key]}</span>
                </div>
              ))}
            </div>
          ) : null}

          {mockDataEnabled !== null && (
            <p className="text-xs text-muted-foreground pb-1">
              Built-in mock-data fallback:{' '}
              <span className="text-foreground font-medium">{mockDataEnabled ? 'enabled' : 'disabled'}</span>
              {mockDataEnabled
                ? ' — empty views show the bundled sample dataset.'
                : ' — flushed views stay empty (real synced data only).'}
            </p>
          )}

          <DialogFooter className="gap-2">
            {statusError ? (
              <Button variant="outline" onClick={refresh} disabled={loading}>
                Retry
              </Button>
            ) : isEmpty ? (
              <Button onClick={handlePopulate} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                Populate Demo Data
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setConfirmAction('reseed')} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                  Re-seed
                </Button>
                <Button variant="destructive" onClick={() => setConfirmAction('flush')} disabled={busy}>
                  Flush
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'reseed' ? 'Re-seed' : 'Flush ALL'} data for {tenant.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'reseed' ? (
                <>Re-seed flushes this tenant completely before populating fresh demo data. It</>
              ) : (
                <>This</>
              )}{' '}
              permanently deletes every row in this tenant's projects, tasks, KPIs, meetings, emails, risk
              signals, documents, decisions, and activity — <strong>seeded or real</strong>. This tool cannot yet
              tell demo rows apart from genuine ones in these tables, so only do this on a tenant with nothing you
              need to keep. Type the tenant slug (<span className="font-mono">{tenant.slug}</span>) to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={tenant.slug} />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction === 'reseed' ? handleReseed : handleFlush}
              disabled={confirmText !== tenant.slug || busy}
            >
              {confirmAction === 'reseed' ? 'Re-seed' : 'Flush'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
